import {
  faAngleDown,
  faAngleUp,
  faSearch,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import {
  KeyboardEvent,
  ReactElement,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { StringParam, useQueryParam } from 'use-query-params';
import { useDebounce } from 'use-debounce';
import { Button, SearchResultsMenu, Tag } from '.';
import useSearch from '../hooks/search';
import {
  SearchQueryFilter,
  SearchResult,
  SearchResults,
} from '../types/search-result';
import classNames from '../utilities/class-names';
import pluralize from '../utilities/pluralize';
import {
  addSearchQueryTypeFilter,
  groupSearchResults,
  removeSearchQueryFilter,
  searchResultUrl,
} from '../utilities/search-results';
import { searchResultsMenuItemId } from './SearchResultsMenu';
import './SearchBar.scss';

// Matches the server's minimum query length
const SEARCH_QUERY_MIN_LENGTH = 3;
const SEARCH_QUERY_MAX_LENGTH = 100;
const SEARCH_DEBOUNCE_MS = 300;

const SHORTCUT_LABEL = /mac|iphone|ipad/i.test(navigator.userAgent)
  ? '⌘K'
  : 'Ctrl+K';

const isEditableElement = (target: EventTarget | null) =>
  target instanceof HTMLElement &&
  (['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) ||
    target.isContentEditable);

export function SearchBar(): ReactElement {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { pending, error, resetError, search } = useSearch();
  const [searchQueryParam, setSearchQueryParam] = useQueryParam(
    'query',
    StringParam
  );

  const [searchQuery, setSearchQuery] = useState(searchQueryParam ?? '');
  const [debouncedSearchQuery, debounceControls] = useDebounce(
    searchQuery.trim(),
    SEARCH_DEBOUNCE_MS
  );
  const [searchResponse, setSearchResponse] = useState<SearchResults | null>(
    null
  );
  const searchResults = searchResponse?.results ?? null;
  const searchFilters = useMemo(
    () => searchResponse?.query.filters ?? [],
    [searchResponse]
  );
  const [searchResultsVisible, setSearchResultsVisible] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Whether results should be shown when they arrive; this is turned off when
  // the user dismisses the menu, so a slow response doesn't re-open it
  const showResultsOnArrival = useRef(!!searchQueryParam);

  const menuId = useId();
  const groups = useMemo(
    () => groupSearchResults(searchResults ?? []),
    [searchResults]
  );
  const orderedResults = useMemo(
    () => groups.flatMap(group => group.results),
    [groups]
  );

  const hasValidQuery = debouncedSearchQuery.length >= SEARCH_QUERY_MIN_LENGTH;
  // The menu also opens when there are no results but the query has filters,
  // so the filters can be seen (and removed)
  const searchResultsMenuOpen =
    searchResultsVisible &&
    hasValidQuery &&
    (orderedResults.length > 0 || searchFilters.length > 0);

  // Search for a query that was put in the url from elsewhere, e.g. by clicking
  // on a tag
  useEffect(() => {
    if (searchQueryParam && searchQueryParam !== debouncedSearchQuery) {
      showResultsOnArrival.current = true;
      setSearchResultsVisible(true);
      setSearchQuery(searchQueryParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQueryParam]);

  const closeResults = useCallback(() => {
    showResultsOnArrival.current = false;
    setSearchResultsVisible(false);
    setActiveIndex(-1);
  }, []);

  // Search as the user types
  useEffect(() => {
    setSearchQueryParam(
      hasValidQuery ? debouncedSearchQuery : undefined,
      'replaceIn'
    );

    if (!hasValidQuery) {
      setSearchResponse(null);
      setActiveIndex(-1);
      return;
    }

    const controller = new AbortController();
    search(debouncedSearchQuery, controller.signal).then(response => {
      if (response) {
        setSearchResponse(response);
        setActiveIndex(-1);
        if (showResultsOnArrival.current) {
          setSearchResultsVisible(true);
        }
      }
    });

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchQuery, search]);

  // Close the results when navigating to another page
  const previousPathname = useRef(pathname);
  useEffect(() => {
    if (pathname !== previousPathname.current) {
      previousPathname.current = pathname;
      closeResults();
    }
  }, [pathname, closeResults]);

  // Close the results when clicking outside the search bar
  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) {
        closeResults();
      }
    };

    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [closeResults]);

  // Focus the search bar with Ctrl+K / Cmd+K, or "/" when not typing elsewhere
  useEffect(() => {
    const handleKeyDown = (e: globalThis.KeyboardEvent) => {
      const isModifierShortcut =
        e.key.toLowerCase() === 'k' && (e.ctrlKey || e.metaKey) && !e.altKey;
      const isSlashShortcut =
        e.key === '/' &&
        !e.ctrlKey &&
        !e.metaKey &&
        !e.altKey &&
        !isEditableElement(e.target);

      if (isModifierShortcut || isSlashShortcut) {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const openResult = useCallback(
    (result: SearchResult) => {
      closeResults();
      inputRef.current?.blur();
      navigate(searchResultUrl(result));
    },
    [closeResults, navigate]
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      showResultsOnArrival.current = true;
      setSearchResultsVisible(true);
      setSearchQuery(e.target.value);
      resetError();
    },
    [resetError]
  );

  const handleReset = useCallback(() => {
    setSearchQuery('');
    setSearchResponse(null);
    closeResults();
    resetError();
    inputRef.current?.focus();
  }, [closeResults, resetError]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      const count = orderedResults.length;

      switch (e.key) {
        case 'ArrowDown':
        case 'ArrowUp':
          if (count === 0) {
            return;
          }
          e.preventDefault();
          if (!searchResultsMenuOpen) {
            showResultsOnArrival.current = true;
            setSearchResultsVisible(true);
            setActiveIndex(e.key === 'ArrowDown' ? 0 : count - 1);
            return;
          }
          setActiveIndex(i =>
            e.key === 'ArrowDown' ? (i + 1) % count : i <= 0 ? count - 1 : i - 1
          );
          break;

        case 'Enter':
          e.preventDefault();
          if (
            searchResultsMenuOpen &&
            count > 0 &&
            !debounceControls.isPending()
          ) {
            openResult(orderedResults[Math.max(activeIndex, 0)]);
          } else {
            // Search immediately instead of waiting for the debounce
            showResultsOnArrival.current = true;
            setSearchResultsVisible(true);
            debounceControls.flush();
          }
          break;

        case 'Escape':
          if (searchResultsMenuOpen) {
            e.preventDefault();
            closeResults();
          } else if (searchQuery) {
            e.preventDefault();
            handleReset();
          }
          break;

        case 'Tab':
          closeResults();
          break;
      }
    },
    [
      orderedResults,
      searchResultsMenuOpen,
      activeIndex,
      searchQuery,
      openResult,
      closeResults,
      handleReset,
      debounceControls,
    ]
  );

  // Editing the query from the menu keeps the results open and returns focus
  // to the search bar
  const editQuery = useCallback((update: (query: string) => string) => {
    showResultsOnArrival.current = true;
    setSearchResultsVisible(true);
    setSearchQuery(update);
    inputRef.current?.focus();
  }, []);

  const handleRemoveFilter = useCallback(
    (filter: SearchQueryFilter) =>
      editQuery(query => removeSearchQueryFilter(query, filter.raw)),
    [editQuery]
  );

  const handleFilterByType = useCallback(
    (type: SearchResult['type']) =>
      editQuery(query => addSearchQueryTypeFilter(query, type)),
    [editQuery]
  );

  const resultCount = searchResults?.length ?? 0;

  return (
    <div className="search-bar" ref={rootRef}>
      <form
        role="search"
        onSubmit={e => {
          e.preventDefault();
          showResultsOnArrival.current = true;
          setSearchResultsVisible(true);
          debounceControls.flush();
        }}
      >
        <input
          ref={inputRef}
          type="text"
          name="query"
          placeholder={`Search... (${SHORTCUT_LABEL})`}
          className={classNames(searchQuery ? 'has-value' : undefined)}
          maxLength={SEARCH_QUERY_MAX_LENGTH}
          autoComplete="off"
          spellCheck={false}
          value={searchQuery}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (resultCount > 0 || searchFilters.length > 0) {
              showResultsOnArrival.current = true;
              setSearchResultsVisible(true);
            }
          }}
          role="combobox"
          aria-label="Search"
          aria-autocomplete="list"
          aria-expanded={searchResultsMenuOpen}
          aria-controls={menuId}
          aria-activedescendant={
            searchResultsMenuOpen && activeIndex >= 0
              ? searchResultsMenuItemId(menuId, activeIndex)
              : undefined
          }
        />
        <Button
          type="reset"
          className={classNames(
            'reset-search-button',
            searchQuery ? 'has-value' : undefined
          )}
          icon={faTimes}
          onClick={handleReset}
          disabled={!searchQuery && !searchResults}
          secondary
        />
        <Button
          type="submit"
          className="submit-search-button"
          icon={faSearch}
          loading={pending}
          disabled={searchQuery.trim().length < SEARCH_QUERY_MIN_LENGTH}
          secondary
        />
        <Tag
          intent={resultCount > 0 ? 'success' : 'warning'}
          show={searchResults !== null && hasValidQuery && !error}
          icon={
            resultCount > 0 || searchFilters.length > 0
              ? searchResultsMenuOpen
                ? faAngleUp
                : faAngleDown
              : null
          }
          onClick={() => {
            if (searchResultsMenuOpen) {
              closeResults();
            } else {
              showResultsOnArrival.current = true;
              setSearchResultsVisible(true);
            }
          }}
        >
          {resultCount > 0
            ? `${resultCount} ${pluralize('result', resultCount)}`
            : 'No results'}
        </Tag>
        <Tag intent="danger" show={!!error}>
          Search failed
        </Tag>
      </form>
      <SearchResultsMenu
        id={menuId}
        groups={groups}
        filters={searchFilters}
        terms={searchResponse?.query.terms}
        activeIndex={activeIndex}
        show={searchResultsMenuOpen}
        onSelect={closeResults}
        onHover={setActiveIndex}
        onRemoveFilter={handleRemoveFilter}
        onFilterByType={handleFilterByType}
      />
    </div>
  );
}
